import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

export type BookingCopyLocale = 'en' | 'pl';

/**
 * Student lesson screens stay in English. Tutor lesson screens stay in Polish.
 * Host lesson tools follow the language switcher.
 */
export function bookingCopyLocaleForPath(pathname: string): BookingCopyLocale | null {
  if (pathname.startsWith('/user/tutor-bookings')) return 'en';
  if (
    pathname.startsWith('/tutor/requests') ||
    pathname.startsWith('/tutor/bookings') ||
    pathname.startsWith('/host/my-tutor-requests')
  ) {
    return 'pl';
  }
  return null;
}

function bindLocale(t: TFunction, lng: BookingCopyLocale): TFunction {
  const bound = (key: string, options?: unknown, extra?: unknown) => {
    if (typeof options === 'string') {
      const rest = extra && typeof extra === 'object' ? (extra as Record<string, unknown>) : {};
      return t(key, { defaultValue: options, lng, ...rest });
    }
    if (options && typeof options === 'object') {
      return t(key, { ...(options as Record<string, unknown>), lng });
    }
    return t(key, { lng });
  };
  return bound as unknown as TFunction;
}

export function useBookingCopyTranslation() {
  const translation = useTranslation();
  const { t, i18n } = translation;
  const bookingCopyLocale =
    typeof window === 'undefined' ? null : bookingCopyLocaleForPath(window.location.pathname);

  const copyT = useMemo(
    () => (bookingCopyLocale ? bindLocale(t, bookingCopyLocale) : t),
    [t, bookingCopyLocale],
  );

  const viewI18n = useMemo(() => {
    if (!bookingCopyLocale) return i18n;
    return new Proxy(i18n, {
      get(target, prop, receiver) {
        if (prop === 'language' || prop === 'resolvedLanguage') return bookingCopyLocale;
        const value = Reflect.get(target, prop, receiver);
        return typeof value === 'function' ? value.bind(target) : value;
      },
    });
  }, [i18n, bookingCopyLocale]);

  return { ...translation, t: copyT, i18n: viewI18n, bookingCopyLocale };
}
